import {
	PEOPLE_COMPANY_DOCUMENT_CATEGORY,
	PEOPLE_COMPANY_DOCUMENT_CATEGORY_NAMES,
} from '../../constants';

export const normalizeCompanyDocumentCategoryName = (name) => {
	return String(name || '').toLowerCase().replace(/[^a-z]/g, '');
};

export const COMPANY_DOCUMENT_CATEGORY_KEY = {
	CONTRACT: PEOPLE_COMPANY_DOCUMENT_CATEGORY.CONTRACT,
	ONBOARDING_FORM: PEOPLE_COMPANY_DOCUMENT_CATEGORY.ONBOARDING_FORM,
	POLICY: PEOPLE_COMPANY_DOCUMENT_CATEGORY.POLICY,
	PROCEDURE: PEOPLE_COMPANY_DOCUMENT_CATEGORY.PROCEDURE,
	TEMPLATE: PEOPLE_COMPANY_DOCUMENT_CATEGORY.TEMPLATE,
	POSITION_DESCRIPTION: PEOPLE_COMPANY_DOCUMENT_CATEGORY.POSITION_DESCRIPTION,
};

export const COMPANY_DOCUMENT_CATEGORY_DEFINITIONS = [
	{ key: COMPANY_DOCUMENT_CATEGORY_KEY.CONTRACT, name: PEOPLE_COMPANY_DOCUMENT_CATEGORY_NAMES[COMPANY_DOCUMENT_CATEGORY_KEY.CONTRACT] },
	{ key: COMPANY_DOCUMENT_CATEGORY_KEY.POSITION_DESCRIPTION, name: PEOPLE_COMPANY_DOCUMENT_CATEGORY_NAMES[COMPANY_DOCUMENT_CATEGORY_KEY.POSITION_DESCRIPTION] },
	{ key: COMPANY_DOCUMENT_CATEGORY_KEY.ONBOARDING_FORM, name: PEOPLE_COMPANY_DOCUMENT_CATEGORY_NAMES[COMPANY_DOCUMENT_CATEGORY_KEY.ONBOARDING_FORM] },
	{ key: COMPANY_DOCUMENT_CATEGORY_KEY.POLICY, name: PEOPLE_COMPANY_DOCUMENT_CATEGORY_NAMES[COMPANY_DOCUMENT_CATEGORY_KEY.POLICY] },
	{ key: COMPANY_DOCUMENT_CATEGORY_KEY.PROCEDURE, name: PEOPLE_COMPANY_DOCUMENT_CATEGORY_NAMES[COMPANY_DOCUMENT_CATEGORY_KEY.PROCEDURE] },
	{ key: COMPANY_DOCUMENT_CATEGORY_KEY.TEMPLATE, name: PEOPLE_COMPANY_DOCUMENT_CATEGORY_NAMES[COMPANY_DOCUMENT_CATEGORY_KEY.TEMPLATE] },
];

export const COMPANY_DOCUMENT_CATEGORY_NAME_BY_KEY = {
	...PEOPLE_COMPANY_DOCUMENT_CATEGORY_NAMES,
};

export const COMPANY_DOCUMENT_ONBOARDING_POLICY_CATEGORY_KEYS = [
	COMPANY_DOCUMENT_CATEGORY_KEY.POLICY,
	COMPANY_DOCUMENT_CATEGORY_KEY.PROCEDURE,
];

const CATEGORY_KEY_BY_NORMALIZED_NAME = {
	contract: COMPANY_DOCUMENT_CATEGORY_KEY.CONTRACT,
	contracts: COMPANY_DOCUMENT_CATEGORY_KEY.CONTRACT,
	onboardingform: COMPANY_DOCUMENT_CATEGORY_KEY.ONBOARDING_FORM,
	onboardingforms: COMPANY_DOCUMENT_CATEGORY_KEY.ONBOARDING_FORM,
	onboardingdocument: COMPANY_DOCUMENT_CATEGORY_KEY.ONBOARDING_FORM,
	form: COMPANY_DOCUMENT_CATEGORY_KEY.ONBOARDING_FORM,
	forms: COMPANY_DOCUMENT_CATEGORY_KEY.ONBOARDING_FORM,
	policy: COMPANY_DOCUMENT_CATEGORY_KEY.POLICY,
	policies: COMPANY_DOCUMENT_CATEGORY_KEY.POLICY,
	procedure: COMPANY_DOCUMENT_CATEGORY_KEY.PROCEDURE,
	procedures: COMPANY_DOCUMENT_CATEGORY_KEY.PROCEDURE,
	sop: COMPANY_DOCUMENT_CATEGORY_KEY.PROCEDURE,
	sops: COMPANY_DOCUMENT_CATEGORY_KEY.PROCEDURE,
	template: COMPANY_DOCUMENT_CATEGORY_KEY.TEMPLATE,
	templates: COMPANY_DOCUMENT_CATEGORY_KEY.TEMPLATE,
	positiondescription: COMPANY_DOCUMENT_CATEGORY_KEY.POSITION_DESCRIPTION,
};

const resolveRawCategoryValue = (value) => {
	if (!value || typeof value !== 'object' || Array.isArray(value)) {
		return value;
	}

	return value.key ?? value.value ?? value.categoryKey ?? value.category ?? value.id ?? value._id ?? value.name ?? null;
};

const toCategoryInt = (value) => {
	if (value === null || value === undefined || value === '') {
		return null;
	}

	if (typeof value === 'number' && Number.isFinite(value)) {
		return value;
	}

	const trimmed = String(value).trim();
	if (!/^-?\d+$/.test(trimmed)) {
		return null;
	}

	const parsed = Number.parseInt(trimmed, 10);
	return Number.isFinite(parsed) ? parsed : null;
};

export const isCompanyDocumentCategoryKey = (value) => {
	const categoryKey = toCategoryInt(resolveRawCategoryValue(value));
	return categoryKey !== null && Object.prototype.hasOwnProperty.call(COMPANY_DOCUMENT_CATEGORY_NAME_BY_KEY, categoryKey);
};

export const getCompanyDocumentCategoryNameByKey = (key) => {
	const categoryKey = toCategoryInt(key);
	return categoryKey !== null ? (COMPANY_DOCUMENT_CATEGORY_NAME_BY_KEY[categoryKey] || '') : '';
};

const compareCategoryDefinitionsByName = (left, right) => (
	String(left?.name || '').localeCompare(String(right?.name || ''), undefined, { sensitivity: 'base' })
);

export const getCompanyDocumentCategorySortRank = (key) => {
	const categoryKey = resolveCompanyDocumentCategoryKey(key);
	if (!categoryKey) {
		return Number.MAX_SAFE_INTEGER;
	}

	const sortedDefinitions = [...COMPANY_DOCUMENT_CATEGORY_DEFINITIONS].sort(compareCategoryDefinitionsByName);
	const index = sortedDefinitions.findIndex(category => category.key === categoryKey);
	return index === -1 ? Number.MAX_SAFE_INTEGER : index;
};

export const getCompanyDocumentCategoryOptions = () => {
	return [...COMPANY_DOCUMENT_CATEGORY_DEFINITIONS].sort(compareCategoryDefinitionsByName).map(category => ({
		label: category.name,
		value: category.key,
	}));
};

export const resolveCompanyDocumentCategoryKey = (value) => {
	const rawValue = resolveRawCategoryValue(value);
	const directInt = toCategoryInt(rawValue);
	if (directInt !== null && isCompanyDocumentCategoryKey(directInt)) {
		return directInt;
	}

	const directValue = String(rawValue || '').trim();
	if (!directValue) {
		return null;
	}

	const normalized = normalizeCompanyDocumentCategoryName(directValue);
	return CATEGORY_KEY_BY_NORMALIZED_NAME[normalized] || null;
};

export const resolveCompanyDocumentCategoryName = (value) => {
	const key = resolveCompanyDocumentCategoryKey(value);
	if (!key) {
		return '';
	}

	return getCompanyDocumentCategoryNameByKey(key);
};

export const isCompanyDocumentContractCategoryKey = (key) => (
	resolveCompanyDocumentCategoryKey(key) === COMPANY_DOCUMENT_CATEGORY_KEY.CONTRACT
);

export const isCompanyDocumentOnboardingFormCategoryKey = (key) => (
	resolveCompanyDocumentCategoryKey(key) === COMPANY_DOCUMENT_CATEGORY_KEY.ONBOARDING_FORM
);

export const isCompanyDocumentTemplateCategoryKey = (key) => (
	resolveCompanyDocumentCategoryKey(key) === COMPANY_DOCUMENT_CATEGORY_KEY.TEMPLATE
);

export const isCompanyDocumentPositionDescriptionCategoryKey = (key) => (
	resolveCompanyDocumentCategoryKey(key) === COMPANY_DOCUMENT_CATEGORY_KEY.POSITION_DESCRIPTION
);

export const isCompanyDocumentPoliciesCategoryKey = (key) => (
	COMPANY_DOCUMENT_ONBOARDING_POLICY_CATEGORY_KEYS.includes(resolveCompanyDocumentCategoryKey(key))
);

export const isCompanyDocumentContractCategoryName = (name) => {
	return isCompanyDocumentContractCategoryKey(name);
};

export const isCompanyDocumentTemplateCategoryName = (name) => {
	return isCompanyDocumentTemplateCategoryKey(name);
};

export const isCompanyDocumentPositionDescriptionCategoryName = (name) => {
	return isCompanyDocumentPositionDescriptionCategoryKey(name);
};
